import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Heart, Loader2, MessageSquare, Package, ShieldCheck, Store, UserCheck, Users } from "lucide-react";
import { getJson } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { locDoc, useLang } from "@/lib/i18n";
import { CATALOG_CATEGORIES } from "@/data/catalog";
import { TURTLES, countryName, flagOf } from "@/data/profile";
import { Avatar } from "@/components/community/shared";
import { Post, useWallState } from "@/components/community/Wall";
import { itemImage } from "@/components/catalog/ItemCard";
import { FollowButton, FollowList } from "@/components/profile/Follow";
import { profileProgress } from "@/pages/ProfileQuiz";

const TABS = ["historia", "coleccion", "mercado", "actividad"];

const Stat = ({ icon: Icon, n, label, testid, onClick }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left ${onClick ? "transition-colors hover:border-[var(--green)]" : ""}`} data-testid={testid}>
      <p className="font-display text-3xl leading-none">{n ?? 0}</p>
      <p className="mono mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]"><Icon size={12} /> {label}</p>
    </Tag>
  );
};

const Header = ({ p, openList }) => {
  const { t, lang } = useLang();
  const turtle = TURTLES[p.favorite];
  const joined = p.created_at ? new Date(p.created_at).toLocaleDateString(lang, { month: "long", year: "numeric" }) : "";
  return (
    <header className="profile-hero" style={{ "--accent": turtle?.color ?? "var(--green)" }} data-testid="public-profile-header">
      <div className="flex flex-wrap items-center gap-6">
        <div className="profile-ring"><Avatar author={p} size={112} /></div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {turtle && <span className="profile-badge" data-testid="profile-favorite">{t("prof.team", { name: turtle.name })}</span>}
            {profileProgress(p) === 5 && (
              <span className="profile-badge items-center gap-1.5" style={{ background: "#e8b64a" }} title={t("prof.completeHint")} data-testid="profile-complete-badge">
                <ShieldCheck size={13} /> {t("prof.complete")}
              </span>
            )}
          </div>
          <h1 className="font-display mt-3 text-5xl leading-none sm:text-6xl" data-testid="public-profile-name">{p.name}</h1>
          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--muted)]">
            {p.country && <span data-testid="profile-country">{flagOf(p.country)} {countryName(p.country, lang)}</span>}
            {p.fan_since && <span data-testid="profile-fan-since">{t("prof.fanSince", { y: p.fan_since })}</span>}
            {joined && <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {t("prof.joined", { d: joined })}</span>}
          </p>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-5 sm:max-w-3xl">
        <Stat icon={Users} n={p.stats.followers} label={t("prof.followers")} testid="profile-stat-followers" onClick={() => openList("followers")} />
        <Stat icon={UserCheck} n={p.stats.following} label={t("prof.following")} testid="profile-stat-following" onClick={() => openList("following")} />
        <Stat icon={Package} n={p.stats.items} label={t("prof.pieces")} testid="profile-stat-items" />
        <Stat icon={MessageSquare} n={p.stats.posts} label={t("prof.posts")} testid="profile-stat-posts" />
        <Stat icon={Heart} n={p.stats.likes} label={t("prof.likes")} testid="profile-stat-likes" />
      </div>
    </header>
  );
};

const Collection = ({ p }) => {
  const { t, lang } = useLang();
  const [cat, setCat] = useState("");
  const items = useMemo(() => p.items.map((i) => locDoc(i, lang)).filter((i) => !cat || i.category === cat), [p.items, lang, cat]);
  if (!p.collection_public) return <p className="text-[var(--muted)]" data-testid="profile-collection-private">{t("prof.private")}</p>;
  if (!p.items.length) return <p className="text-[var(--muted)]" data-testid="profile-collection-empty">{t("prof.noItems")}</p>;
  const cats = CATALOG_CATEGORIES.filter((c) => p.items.some((i) => i.category === c.id));
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {[{ id: "" }, ...cats].map((c) => (
          <button key={c.id || "all"} type="button" onClick={() => setCat(c.id)} className={`col-chip ${cat === c.id ? "col-chip-on" : ""}`} data-testid={`profile-cat-${c.id || "all"}`}>{c.id ? t(`cat.${c.id}`) : t("col.all")}</button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((it) => (
          <div key={it.id} className="vitrine" data-testid={`profile-item-${it.id}`}>
            <div className="vitrine-case !aspect-square"><span className="vitrine-lamp" /><span className="vitrine-cone" /><img src={itemImage(it)} alt={it.name} className="vitrine-obj" loading="lazy" /><span className="vitrine-base" /></div>
            <p className="font-display mt-3 line-clamp-2 text-lg leading-tight">{it.name}</p>
            <p className="mt-1 truncate text-xs text-[var(--muted)]">{it.manufacturer} · {it.year}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const Activity = ({ p }) => {
  const { t } = useLang();
  const [posts, setPosts] = useState(p.posts);
  const ws = useWallState(setPosts);
  if (!p.topics.length && !posts.length) return <p className="text-[var(--muted)]" data-testid="profile-activity-empty">{t("prof.noActivity")}</p>;
  return (
    <div className="grid gap-10 lg:grid-cols-2">
      <div>
        <h3 className="font-display text-2xl">{t("com.tab.foros")}</h3>
        {p.topics.map((tp) => (
          <Link key={tp.id} to={`/comunidad/tema/${tp.id}`} className="block border-b border-white/10 py-4 hover:text-[var(--green)]" data-testid={`profile-topic-${tp.id}`}>
            <p className="mono text-[11px] uppercase tracking-[0.14em] text-[var(--green)]">{t(`fcat.${tp.category}`)}</p>
            <p className="font-display mt-1 text-xl">{tp.title}</p>
          </Link>
        ))}
      </div>
      <div>
        <h3 className="font-display mb-4 text-2xl">{t("com.tab.pared")}</h3>
        {posts.map((x) => <Post key={x.id} p={x} ws={ws} />)}
      </div>
    </div>
  );
};

export default function PublicProfile() {
  const { handle } = useParams();
  const { t } = useLang();
  const { member } = useAuth();
  const [p, setP] = useState(undefined);
  const [tab, setTab] = useState("historia");
  const [rel, setRel] = useState(null);
  useEffect(() => {
    setRel(null);
    getJson(`/u/${handle}`).then(setP);
  }, [handle, member]);
  const onFollow = (r) => setP((s) => ({ ...s, is_following: r.is_following, stats: { ...s.stats, followers: r.followers } }));
  if (p === undefined) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin" /></main>;
  if (!p) return <main className="px-5 pt-40 text-center"><p className="font-display text-4xl" data-testid="public-profile-not-found">{t("prof.notFound")}</p></main>;
  return (
    <main className="px-5 pb-28 pt-28 md:px-10" data-testid="public-profile-page">
      <div className="mx-auto max-w-6xl">
        <Header p={p} openList={setRel} />
        {member === null ? null : member?.member_id === p.member_id
          ? <Link to="/perfil" className="btn-ghost mt-6" data-testid="public-profile-edit-btn">{t("prof.edit")}</Link>
          : <FollowButton p={p} onChange={onFollow} />}
        <FollowList handle={handle} rel={rel} onClose={() => setRel(null)} />
        <div className="mt-10 flex gap-1 overflow-x-auto rounded-full border border-white/15 bg-white/5 p-1 sm:inline-flex" role="tablist" data-testid="public-profile-tabs">
          {TABS.map((k) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`col-mode shrink-0 ${tab === k ? "col-mode-on" : ""}`} data-testid={`profile-tab-${k}`}>{t(`prof.tab.${k}`)}</button>)}
        </div>
        <section className="mt-10" data-testid={`profile-panel-${tab}`}>
          {tab === "historia" && (p.bio ? <p className="font-display max-w-3xl whitespace-pre-line text-2xl leading-snug md:text-3xl" data-testid="profile-bio">{p.bio}</p> : <p className="text-[var(--muted)]">{t("prof.noBio")}</p>)}
          {tab === "coleccion" && <Collection p={p} />}
          {tab === "mercado" && <div className="flex items-center gap-3 text-[var(--muted)]" data-testid="profile-market"><Store size={18} /> {t("com.marketSoon")}</div>}
          {tab === "actividad" && <Activity p={p} />}
        </section>
      </div>
    </main>
  );
}
